import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AumicoAbcpConfig, AumicoAbcpConfigService } from '../abcp-data.config';
import {
    IAdjustmentAccountListResponse,
    IAdjustmentDetailResponse,
    IAdjustmentListResponse,
    IAdjustmentTransferResponse,
    ICreateAdjustmentRequest,
    IAdjustmentListItem,
    IEditAdjustmentRequest,
} from '../models/closing-adjustment.model';
import { AdjustmentSource, AdjustmentState, AdjustmentTransferMethod } from '../types/closing-adjustments.types';

/**
 * HTTP for the ABCP correction bookings — REV-21375, `ClosingAdjustmentsController` in `aumico-be`.
 *
 * ⚠️ **There is NO method that sets `Gebucht`, and there must not be** (ADR-0015 §2). The transition
 * comes out of the imported ERP journal. {@link resolveAmbiguity} is the single exception and does not
 * break the rule: it names WHICH of the candidates the reconciliation already found is the proof.
 *
 * ⚠️ **There is NO delete.** Discarding is a state (§7), and it is a `POST …/discard` rather than a
 * `DELETE …/:id` so that no layer can confuse the two.
 *
 * ⚠️ **There is no accept and no reject.** The agent proposes no corrections in this flow (§8), so
 * nothing can be in a state where either would apply.
 */
@Injectable()
export class ClosingAdjustmentService {
    private readonly baseUrl: string;

    public constructor(
        private readonly http: HttpClient,
        @Inject(AumicoAbcpConfigService) config: AumicoAbcpConfig,
    ) {
        // NO leading slash: `apiGateway` already ENDS with one (`http://localhost:3800/api/`), so
        // `${config.apiGateway}/projects` yields `/api//projects` and the gateway 404s. Same
        // concatenation as `closing-document.service.ts`.
        this.baseUrl = `${config.apiGateway}projects`;
    }

    /**
     * The closing-wide read: the rows of one fiscal year, ALL FOUR counts, the three derived result
     * figures, and what the transfer control may offer.
     *
     * `fiscalYear` is omitted rather than sent as the current year: the backend knows which year is
     * current (it reads the closing's balance-sheet date), and a client that computed it from the clock
     * would be wrong for every mandate whose fiscal year is not the calendar year.
     *
     * ⚠️ `state` narrows the ROWS and never the counts — the four pills each show their own total, and
     * a count that moved with the active pill would always equal that pill's row count.
     */
    /**
     * ⚠️ `discarded` is a separate parameter and not a `state` value, because it is a different
     * question: the state narrows how far a booking travelled, this flips which population is read.
     * Sending both is meaningful — "the withdrawn ones that were released" — and the server honours it.
     */
    public listAdjustments(
        projectId: string,
        fiscalYear: number | null,
        state: AdjustmentState | null,
        discarded = false,
    ): Observable<IAdjustmentListResponse> {
        let params = new HttpParams();
        if (fiscalYear !== null) {
            params = params.set('fiscalYear', String(fiscalYear));
        }
        if (discarded) {
            params = params.set('discarded', 'true');
        }
        if (state !== null) {
            params = params.set('state', state.toUpperCase());
        }

        return this.http
            .get<IAdjustmentListResponse>(`${this.baseUrl}/${projectId}/adjustments`, { params })
            .pipe(
                map((response) => ({
                    ...response,
                    adjustments: response.adjustments.map((item) =>
                        ClosingAdjustmentService.fromWire(item, response.readOnly),
                    ),
                })),
            );
    }

    /**
     * The corrections attributed to ONE account (REV-21364's read).
     *
     * Its own route, with the ACCOUNT as the parent, because that is what the read is scoped by. Its
     * own method for the same reason `listAccountDocuments` has one next door: the closing-wide surface
     * must never be able to send an account by accident and quietly show a fraction of the closing.
     */
    public listAccountAdjustments(
        projectId: string,
        accountId: string,
        fiscalYear: number | null,
    ): Observable<IAdjustmentAccountListResponse> {
        let params = new HttpParams();
        if (fiscalYear !== null) {
            params = params.set('fiscalYear', String(fiscalYear));
        }

        return this.http
            .get<IAdjustmentAccountListResponse>(
                `${this.baseUrl}/${projectId}/accounts/${accountId}/adjustments`,
                { params },
            )
            .pipe(
                map((response) => ({
                    ...response,
                    adjustments: response.adjustments.map((item) =>
                        ClosingAdjustmentService.fromWire(item, response.readOnly),
                    ),
                })),
            );
    }

    /** The detail surface, including the candidate lines when the match is ambiguous. */
    public getAdjustment(projectId: string, adjustmentId: string): Observable<IAdjustmentDetailResponse> {
        return this.http
            .get<IAdjustmentDetailResponse>(`${this.baseUrl}/${projectId}/adjustments/${adjustmentId}`)
            .pipe(
                map((response) => ({
                    ...response,
                    // The detail surface has no `readOnly` of its own: a prior-year row is recognised
                    // by its fiscal year, and `VJ` is a LIST affordance. Left as the wire says.
                    adjustment: ClosingAdjustmentService.fromWire(response.adjustment, false),
                })),
            );
    }

    /** Enter a correction BY HAND — the only way one comes into existence (§6). */
    public createAdjustment(
        projectId: string,
        request: ICreateAdjustmentRequest,
    ): Observable<IAdjustmentDetailResponse> {
        return this.http.post<IAdjustmentDetailResponse>(`${this.baseUrl}/${projectId}/adjustments`, request);
    }

    /**
     * Edit an OPEN correction.
     *
     * `PATCH`, not `PUT`: the body carries what CHANGED, and the two surfaces edit different subsets —
     * the list's modal has no remark field, the detail surface's inline form does.
     */
    public editAdjustment(
        projectId: string,
        adjustmentId: string,
        request: IEditAdjustmentRequest,
    ): Observable<IAdjustmentDetailResponse> {
        return this.http.patch<IAdjustmentDetailResponse>(
            `${this.baseUrl}/${projectId}/adjustments/${adjustmentId}`,
            request,
        );
    }

    /**
     * Discard an OPEN correction — a STATE, never a delete (§7).
     *
     * A `POST …/discard` and not a `DELETE …/:id`: the verb is what a reader and a client library take
     * the operation to MEAN, and `DELETE` on a correction would mean the one thing this flow never does.
     */
    /** `Verworfen` → `Freigegeben`. A POST: a named transition, not a patch of a field. */
    public restoreAdjustment(projectId: string, adjustmentId: string): Observable<IAdjustmentDetailResponse> {
        return this.http.post<IAdjustmentDetailResponse>(
            `${this.baseUrl}/${projectId}/adjustments/${adjustmentId}/restore`,
            {},
        );
    }

    public discardAdjustment(projectId: string, adjustmentId: string): Observable<IAdjustmentDetailResponse> {
        return this.http.post<IAdjustmentDetailResponse>(
            `${this.baseUrl}/${projectId}/adjustments/${adjustmentId}/discard`,
            {},
        );
    }

    /**
     * Transfer a SELECTION — one of three targets, one state transition (§3).
     *
     * `Idempotency-Key` is a HEADER, because it is a property of the request rather than of the
     * selection: a retry of the same request carries the same key, while a new selection is a new
     * request. For bookings a double delivery is not a blemish, which is why it is sent at all.
     */
    public transferAdjustments(
        projectId: string,
        adjustmentIds: string[],
        method: AdjustmentTransferMethod,
        idempotencyKey: string,
    ): Observable<IAdjustmentTransferResponse> {
        return this.http.post<IAdjustmentTransferResponse>(
            `${this.baseUrl}/${projectId}/adjustments/transfer`,
            { adjustmentIds, method: this.wireMethod(method) },
            { headers: { 'Idempotency-Key': idempotencyKey } },
        );
    }

    /**
     * Resolve an AMBIGUOUS journal match by naming ONE of the candidates the reconciliation found (§4).
     *
     * ⚠️ `journalEntryId` is never invented by the client: the backend refuses anything that was not
     * among the candidates it served, so the proof keeps coming from the journal.
     */
    public resolveAmbiguity(
        projectId: string,
        adjustmentId: string,
        journalEntryId: string,
    ): Observable<IAdjustmentDetailResponse> {
        return this.http.post<IAdjustmentDetailResponse>(
            `${this.baseUrl}/${projectId}/adjustments/${adjustmentId}/resolve-ambiguity`,
            { journalEntryId },
        );
    }

    /**
     * The view's camelCase method to the API's `TransferMethod` enum.
     *
     * A rename at the WIRE edge and nowhere else: the views speak camelCase like every other FE type,
     * and the backend's enum is UPPER_SNAKE. Doing it here keeps both sides idiomatic and puts the one
     * place they meet under a single function.
     */
    /**
     * Wire vocabulary to the FE's, per row — the INBOUND twin of {@link wireMethod}.
     *
     * The backend's enums are UPPER_SNAKE (`OPEN`, `MANUAL`); every FE type in this library is
     * lowercase. Only the outbound half of that was ever done -- `state.toUpperCase()` on the query
     * above -- so responses arrived untranslated and two columns quietly stopped working: the status
     * badge was handed `OPEN`, matched none of its three states and rendered broken, and
     * `SOURCE_LABEL_KEYS['MANUAL']` was `undefined`, which is an EMPTY `Quelle` cell rather than a
     * visible error.
     *
     * An unknown value is passed through unchanged rather than defaulted. A new state the FE has not
     * been taught should look wrong on screen, not silently read as `open`.
     */
    private static fromWire(item: IAdjustmentListItem, readOnly: boolean): IAdjustmentListItem {
        const state = ClosingAdjustmentService.WIRE_STATE[String(item.state).toUpperCase()];
        const source = ClosingAdjustmentService.WIRE_SOURCE[String(item.source).toUpperCase()];

        return {
            ...item,
            state: state ?? item.state,
            // `VJ` in a prior year (AC1 of 21370). The backend has no `priorYear` member and should not
            // grow one: it is not a second WRITER, it is how this list labels a year it cannot touch.
            source: readOnly ? 'priorYear' : source ?? item.source,
        };
    }

    /** UPPER_SNAKE on the wire, lowercase in this library. */
    private static readonly WIRE_STATE: Record<string, AdjustmentState | undefined> = {
        PROPOSED: 'proposed',
        RELEASED: 'released',
        TRANSFERRED: 'transferred',
        BOOKED: 'booked',
    };

    private static readonly WIRE_SOURCE: Record<string, AdjustmentSource | undefined> = {
        USER: 'user',
        AGENT: 'agent',
    };

    private wireMethod(method: AdjustmentTransferMethod): string {
        switch (method) {
            case 'erpApi':
                return 'ERP_API';
            case 'markedManual':
                return 'MARKED_MANUAL';
        }
    }
}
